import { useEffect, useRef } from "react";
import { equipmentSurveyApiResponseToFormState } from "./Survey.helpers";

export function shouldHydrateFromAgentPrefill(
    justOpened: boolean,
    agentPrefillFingerprint: string,
    lastAgentPrefillFingerprint: string | null
): boolean {
    return justOpened || agentPrefillFingerprint !== lastAgentPrefillFingerprint;
}

export interface UseEquipmentSurveyOpenHydrationParams {
    showSurveyView: boolean;
    singleFormModalOpen: boolean;
    persistedEquipmentSurvey: EquipmentSurvey | null | undefined;
    agentPrefillEquipmentSurvey: Partial<EquipmentSurvey> | null | undefined;
    pushEquipmentSurveyToFormState: (data: EquipmentSurvey) => void;
    resetFormToDefaults: () => void;
    resetWizardNavigation: () => void;
}

export function useEquipmentSurveyOpenHydration({
    showSurveyView,
    singleFormModalOpen,
    persistedEquipmentSurvey,
    agentPrefillEquipmentSurvey,
    pushEquipmentSurveyToFormState,
    resetFormToDefaults,
    resetWizardNavigation,
}: UseEquipmentSurveyOpenHydrationParams): void {
    const wasSurveyUiOpen = useRef(false);
    const lastAgentPrefillFingerprint = useRef<string | null>(null);
    const lastAgentPrefillRef = useRef<Partial<EquipmentSurvey> | null | undefined>(null);

    useEffect(() => {
        const surveyUiOpen = showSurveyView || singleFormModalOpen;
        const wasOpen = wasSurveyUiOpen.current;
        const justOpened = surveyUiOpen && !wasOpen;
        const justClosed = !surveyUiOpen && wasOpen;
        wasSurveyUiOpen.current = surveyUiOpen;

        if (justClosed) {
            resetFormToDefaults();
            lastAgentPrefillFingerprint.current = null;
            lastAgentPrefillRef.current = null;
            return;
        }
        if (!surveyUiOpen) return;

        const agentPrefillFingerprint = agentPrefillEquipmentSurvey
            ? JSON.stringify(agentPrefillEquipmentSurvey)
            : null;

        if (agentPrefillEquipmentSurvey && agentPrefillFingerprint !== null) {
            const prefillReferenceChanged = lastAgentPrefillRef.current !== agentPrefillEquipmentSurvey;
            const shouldHydrate =
                prefillReferenceChanged ||
                shouldHydrateFromAgentPrefill(justOpened, agentPrefillFingerprint, lastAgentPrefillFingerprint.current);
            if (!shouldHydrate) {
                return;
            }

            lastAgentPrefillFingerprint.current = agentPrefillFingerprint;
            lastAgentPrefillRef.current = agentPrefillEquipmentSurvey;

            const hydrated = equipmentSurveyApiResponseToFormState(agentPrefillEquipmentSurvey as EquipmentSurvey);
            if (!hydrated) return;

            resetWizardNavigation();
            resetFormToDefaults();
            pushEquipmentSurveyToFormState(hydrated);
            return;
        }

        if (!justOpened) return;
        lastAgentPrefillRef.current = agentPrefillEquipmentSurvey;
        const hydrated = equipmentSurveyApiResponseToFormState(persistedEquipmentSurvey);
        if (hydrated) pushEquipmentSurveyToFormState(hydrated);
    }, [
        showSurveyView,
        singleFormModalOpen,
        persistedEquipmentSurvey,
        agentPrefillEquipmentSurvey,
        pushEquipmentSurveyToFormState,
        resetFormToDefaults,
        resetWizardNavigation,
    ]);
}
